import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, FileText, AlertCircle } from 'lucide-react';
import { SearchPanel, Logo } from './components/search/SearchPanel';
import type { SearchOptions } from './types';
import { ProgressBar } from './components/stats/StatsRow';
import { PapersTable } from './components/table/PapersTable';
import { CitationChart, YearChart } from './components/charts/Charts';
import {
  searchPapers,
  startAnalysis,
  pollTaskStatus,
  type Paper,
  type AnalyzedPaper,
  type TaskStatus
} from './api/client';

// 转换 Paper 到 AnalyzedPaper（初始状态）
function convertToAnalyzedPapers(papers: Paper[]): AnalyzedPaper[] {
  return papers.map(paper => ({
    ...paper,
    analysis_status: 'pending',
    analysis: undefined
  }));
}

// 生成图表数据
function generateChartData(papers: AnalyzedPaper[]) {
  const citationData = papers
    .map(p => ({ name: p.title.slice(0, 20) + '...', value: p.citation_count }))
    .sort((a, b) => a.value - b.value);

  const yearCounts: Record<string, number> = {};
  papers.forEach(p => {
    const year = String(p.year);
    yearCounts[year] = (yearCounts[year] || 0) + 1;
  });
  const yearData = Object.entries(yearCounts)
    .map(([year, count]) => ({ year, count }))
    .sort((a, b) => a.year.localeCompare(b.year));

  return { citationData, yearData };
}

function App() {
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [benchmark, setBenchmark] = useState('');
  const [papers, setPapers] = useState<AnalyzedPaper[]>([]);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastOptions, setLastOptions] = useState<SearchOptions | null>(null);

  // 处理搜索
  const handleSearch = useCallback(async (searchTerm: string, options: SearchOptions) => {
    setIsSearching(true);
    setError(null);
    setBenchmark(searchTerm);
    setLastOptions(options);

    try {
      // 1. 搜索论文
      const searchResponse = await searchPapers({
        benchmark: searchTerm,
        limit: options.limit,
        sort_by: options.sortBy
      });

      if (searchResponse.papers.length === 0) {
        setError(`未找到关于 "${searchTerm}" 的论文，请尝试其他数据集名称。`);
        setIsSearching(false);
        return;
      }

      // 2. 转换并设置初始状态
      const analyzedPapers = convertToAnalyzedPapers(searchResponse.papers);
      setPapers(analyzedPapers);
      setHasSearched(true);
      setIsSearching(false);

      // 3. 启动分析任务
      const startResponse = await startAnalysis({
        papers: searchResponse.papers,
        benchmark: searchTerm,
        options: {
          max_workers: options.maxWorkers,
          download_pdf: true
        }
      });

      setTaskId(startResponse.task_id);

      // 4. 轮询任务状态
      await pollTaskStatus(
        startResponse.task_id,
        (status) => {
          setTaskStatus(status);
          setPapers(status.papers);
        },
        1000 // 每秒轮询一次
      );

    } catch (err) {
      setError(err instanceof Error ? err.message : '发生未知错误');
      setIsSearching(false);
    }
  }, []);

  // 导出 CSV
  const exportCSV = useCallback(() => {
    const headers = ['论文标题', '年份', '引用数', '方法', '创新点', '准确率', '性能指标', '作者', '论文链接', 'PDF链接'];
    const rows = papers.map(p => [
      p.title,
      p.year,
      p.citation_count,
      p.analysis?.method || '',
      p.analysis?.innovation || '',
      p.analysis?.accuracy || '',
      p.analysis?.metrics || '',
      p.authors,
      p.url,
      p.pdf_url || ''
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${benchmark}_papers_analysis.csv`;
    link.click();
  }, [papers, benchmark]);

  // 导出 Markdown
  const exportMarkdown = useCallback(() => {
    const lines = [`# ${benchmark} 论文分析报告\n`, `生成时间: ${new Date().toLocaleString()}\n`, '## 论文列表\n'];

    papers.forEach((p, i) => {
      lines.push(`### ${i + 1}. ${p.title}\n`);
      lines.push(`- **年份:** ${p.year}`);
      lines.push(`- **引用数:** ${p.citation_count}`);
      lines.push(`- **作者:** ${p.authors}`);
      lines.push(`- **方法:** ${p.analysis?.method || 'N/A'}`);
      lines.push(`- **创新点:** ${p.analysis?.innovation || 'N/A'}`);
      lines.push(`- **准确率:** ${p.analysis?.accuracy || 'N/A'}`);
      lines.push(`- **性能指标:** ${p.analysis?.metrics || 'N/A'}`);
      lines.push(`- **论文链接:** ${p.url}`);
      if (p.pdf_url) lines.push(`- **PDF链接:** ${p.pdf_url}`);
      lines.push('');
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${benchmark}_papers_analysis.md`;
    link.click();
  }, [papers, benchmark]);

  // 计算图表数据
  const { citationData, yearData } = hasSearched ? generateChartData(papers) : { citationData: [], yearData: [] };

  // 计算统计数据
  const completedCount = papers.filter(p =>
    p.analysis_status === 'completed' ||
    p.analysis_status === 'simple_only' ||
    p.analysis_status === 'failed'
  ).length;
  const analyzingCount = papers.filter(p =>
    p.analysis_status === 'analyzing_simple' ||
    p.analysis_status === 'analyzing_fulltext' ||
    p.analysis_status === 'pending'
  ).length;
  // 简单分析完成但全文分析中的论文也算在分析中
  const simpleCompletedCount = papers.filter(p =>
    p.analysis_status === 'simple_completed'
  ).length;
  const totalAnalyzing = analyzingCount + simpleCompletedCount;
  const progress = papers.length > 0 ? Math.round((completedCount / papers.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <AnimatePresence mode="wait">
        {!hasSearched ? (
          // 搜索前：谷歌首页风格
          <motion.div
            key="hero"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex flex-col"
          >
            <main className="flex-1 flex flex-col items-center justify-center px-4 -mt-20">
              <Logo hasSearched={false} />
              <div className="w-full max-w-2xl">
                <SearchPanel
                  onSearch={handleSearch}
                  isSearching={isSearching}
                  hasSearched={false}
                />
              </div>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg"
                >
                  <AlertCircle size={16} />
                  <span className="text-sm">{error}</span>
                </motion.div>
              )}
            </main>

            <footer className="border-t border-gray-200 bg-white">
              <div className="max-w-6xl mx-auto px-4 py-4 text-center text-sm text-gray-400">
                Benchmark论文追踪工具 - 应用统计学 - 第9组 龚,邵
              </div>
            </footer>
          </motion.div>
        ) : (
          // 搜索后：结果页面
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-screen"
          >
            {/* 顶部栏 */}
            <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
              <div className="max-w-7xl mx-auto px-4 py-3">
                <div className="flex items-center gap-4">
                  <Logo hasSearched={true} />
                  <div className="flex-1 max-w-2xl">
                    <SearchPanel
                      onSearch={handleSearch}
                      isSearching={isSearching}
                      hasSearched={true}
                    />
                  </div>
                </div>
              </div>
            </header>

            {/* 搜索结果内容 */}
            <main className="max-w-7xl mx-auto px-4 py-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                {/* 成功提示 */}
                <div className="mb-4 flex items-center gap-2 text-[#94070a] text-sm">
                  <span className="font-bold">✓</span>
                  <span>找到 {papers.length} 篇相关论文</span>
                  <span className="text-gray-400">（{benchmark}）</span>
                  <span className="text-xs text-gray-500 ml-2">
                    <span className="text-orange-600">●</span>摘要分析
                    <span className="text-green-600 ml-1">●</span>全文分析
                  </span>
                </div>

                {/* 错误提示 */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mb-4 flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-lg text-sm"
                  >
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </motion.div>
                )}

                {/* 进度条 - 只要有论文在分析中就显示 */}
                {(analyzingCount > 0 || simpleCompletedCount > 0) && (
                  <ProgressBar
                    progress={progress}
                    total={papers.length}
                    completed={completedCount}
                  />
                )}

                {/* 论文表格 */}
                <section className="mt-6">
                  <PapersTable papers={papers} benchmark={benchmark} />
                </section>

                {/* 可视化图表 */}
                {papers.length > 0 && (
                  <section className="mt-8">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                      <span className="w-1 h-5 bg-[#94070a] rounded-full"></span>
                      可视化分析
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <CitationChart data={citationData} />
                      <YearChart data={yearData} />
                    </div>
                  </section>
                )}

                {/* 导出 */}
                {completedCount > 0 && (
                  <motion.section
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="mt-8 bg-white p-5 rounded-xl border border-gray-200"
                  >
                    <h2 className="text-base font-semibold text-gray-800 mb-3">导出结果</h2>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={exportCSV}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#94070a] text-white rounded-lg hover:bg-[#7a0608] transition-colors text-sm font-medium"
                      >
                        <Download size={16} />
                        下载 CSV
                      </button>
                      <button
                        onClick={exportMarkdown}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-red-50 hover:border-[#94070a] hover:text-[#94070a] transition-colors text-sm font-medium"
                      >
                        <Download size={16} />
                        下载 Markdown
                      </button>
                    </div>
                  </motion.section>
                )}
              </motion.div>
            </main>

            {/* Footer */}
            <footer className="border-t border-gray-200 bg-white mt-12">
              <div className="max-w-7xl mx-auto px-4 py-4 text-center text-sm text-gray-400">
                Benchmark论文追踪工具 - 应用统计学 - 第9组 龚,邵
              </div>
            </footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
